import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    IconButton,
    Grid,
    TextField,
    Collapse,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    CircularProgress,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import {
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Save as SaveIcon,
    AccountBalance as AccountBalanceIcon,
    Assessment as AssessmentIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import {
    expedienteConcursoJWT, guardarConcursoJWT,
    expedienteAcreedoresJWT, crearAcreedorJWT, editarAcreedorJWT, eliminarAcreedorJWT,
} from '../../api/endpoints';
import { normalizeKeys } from '../../utils/normalizeKeys';
import usePermisosModulo from '../../hooks/usePermisosModulo';

const toISO = (v) => {
    if (!v) return '';
    const s = String(v).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.substring(0, 10);
    const d = new Date(s);
    if (!isNaN(d.valueOf())) {
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${d.getFullYear()}-${mm}-${dd}`;
    }
    return '';
};

const ConcursosTab = ({ idExpediente, tipo, modulo }) => {
    // ---------------- Permisos por módulo ----------------
    const { permDe, ready: permReady } = usePermisosModulo(modulo);
    const permConc = permDe("PESTAÑA CONCURSOS Y QUIEBRAS", "CONCURSOS Y QUIEBRAS");
    const perm = { ready: permReady, ...permConc };

    // Estados principales
    const [loading, setLoading] = useState(false);
    const [expandedSections, setExpandedSections] = useState({
        datos: true,
        concurso: true,
        quiebra: true
    });

    // Datos del formulario principal
    const [formData, setFormData] = useState({
        propuesta: '',
        resultado_acuerdo: '',
        f_presentacion_concurso_preventivo: '',
        f_sentencia_concurso_preventivo: '',
        f_sentencia_declaracion_quiebra: '',
        f_limite_verificacion_credito: '',
        f_presentacion_informe_individual: '',
        f_presentacion_informe_general: '',
        f_sentencia_verificacion: '',
        f_iniciacion_concurso_especial: '',
        f_presentacion_recurso_revision: '',
        f_periodo_exclusividad: '',
        f_periodo_informativo: '',
        f_sentencia_declaracion_quiebra_indirecta: '',
        f_sentencia_verificacion_credito: '',
        incidente_verificacion_credito_posconcursal: '',
        incidente_recurso_revision: '',
        observaciones: '',
        sindico_nombre: '',
        sindico_domicilio: ''
    });

    const [modoEdicion, setModoEdicion] = useState(false);
    const [existe, setExiste] = useState(false);

    // Acreedores
    const [acreedoresConcurso, setAcreedoresConcurso] = useState([]);
    const [acreedoresQuiebra, setAcreedoresQuiebra] = useState([]);

    // Dialog de acreedores
    const [dialogAcreedor, setDialogAcreedor] = useState({
        open: false,
        grilla: '',
        modo: 'crear',
        data: null
    });

    // Dialog de confirmación eliminar
    const [eliminarDialog, setEliminarDialog] = useState({ open: false, nombre: '', onConfirm: null });

    const [formAcreedor, setFormAcreedor] = useState({
        acreedor: '',
        monto: '',
        privilegio: '',
        quirografario: ''
    });

    // Snackbar
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // ============================================
    // CARGA INICIAL
    // ============================================
    useEffect(() => {
        cargarDatos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idExpediente]);

    const cargarDatos = async () => {
        setLoading(true);
        try {
            const [concResp, acrCResp, acrQResp] = await Promise.all([
                expedienteConcursoJWT(idExpediente),
                expedienteAcreedoresJWT(idExpediente, 'C'),
                expedienteAcreedoresJWT(idExpediente, 'Q'),
            ]);
            if (concResp?.ok && concResp.data) {
                const d = normalizeKeys(concResp.data);
                setFormData({
                    propuesta: d.propuesta || '',
                    resultado_acuerdo: d.resultado_acuerdo || '',
                    f_presentacion_concurso_preventivo: toISO(d.f_presentacion_concurso_preventivo),
                    f_sentencia_concurso_preventivo: toISO(d.f_sentencia_concurso_preventivo),
                    f_sentencia_declaracion_quiebra: toISO(d.f_sentencia_declaracion_quiebra),
                    f_limite_verificacion_credito: toISO(d.f_limite_verificacion_credito),
                    f_presentacion_informe_individual: toISO(d.f_presentacion_informe_individual),
                    f_presentacion_informe_general: toISO(d.f_presentacion_informe_general),
                    f_sentencia_verificacion: toISO(d.f_sentencia_verificacion),
                    f_iniciacion_concurso_especial: toISO(d.f_iniciacion_concurso_especial),
                    f_presentacion_recurso_revision: toISO(d.f_presentacion_recurso_revision),
                    f_periodo_exclusividad: toISO(d.f_periodo_exclusividad),
                    f_periodo_informativo: toISO(d.f_periodo_informativo),
                    f_sentencia_declaracion_quiebra_indirecta: toISO(d.f_sentencia_declaracion_quiebra_indirecta),
                    f_sentencia_verificacion_credito: toISO(d.f_sentencia_verificacion_credito),
                    incidente_verificacion_credito_posconcursal: d.incidente_verificacion_credito_posconcursal || '',
                    incidente_recurso_revision: d.incidente_recurso_revision || '',
                    observaciones: d.observaciones || '',
                    sindico_nombre: d.sindico_nombre || '',
                    sindico_domicilio: d.sindico_domicilio || '',
                });
                setExiste(true);
                setModoEdicion(false);
            } else {
                setExiste(false);
                setModoEdicion(true);
            }
            setAcreedoresConcurso(normalizeKeys(acrCResp?.data || []));
            setAcreedoresQuiebra(normalizeKeys(acrQResp?.data || []));
        } catch (error) {
            console.error('Error al cargar datos:', error);
        } finally {
            setLoading(false);
        }
    };

    // ============================================
    // MANEJO DE DATOS PRINCIPALES
    // ============================================
    const handleGuardarDatos = async () => {
        try {
            const resp = await guardarConcursoJWT(idExpediente, formData);
            if (resp?.ok) {
                setSnackbar({ open: true, message: 'Concurso guardado correctamente', severity: 'success' });
                setExiste(true);
                setModoEdicion(false);
            } else {
                setSnackbar({ open: true, message: resp?.mensaje || 'Error al guardar', severity: 'error' });
            }
        } catch (error) {
            console.error('Error al guardar concurso:', error);
            setSnackbar({ open: true, message: 'Error al guardar concurso', severity: 'error' });
        }
    };

    const handleCancelar = () => {
        cargarDatos();
        setModoEdicion(false);
    };

    // ============================================
    // MANEJO DE ACREEDORES
    // ============================================
    const handleAbrirDialogAcreedor = (grilla, acreedor = null) => {
        if (acreedor) {
            setDialogAcreedor({ open: true, grilla, modo: 'editar', data: acreedor });
            setFormAcreedor({
                acreedor: acreedor.acreedor,
                monto: acreedor.monto,
                privilegio: acreedor.privilegio,
                quirografario: acreedor.quirografario
            });
        } else {
            setDialogAcreedor({ open: true, grilla, modo: 'crear', data: null });
            setFormAcreedor({
                acreedor: '',
                monto: '',
                privilegio: '',
                quirografario: ''
            });
        }
    };

    const handleGuardarAcreedor = async () => {
        try {
            const payload = { ...formAcreedor, grilla: dialogAcreedor.grilla };
            let resp;
            if (dialogAcreedor.modo === 'editar' && dialogAcreedor.data?.id) {
                resp = await editarAcreedorJWT(idExpediente, dialogAcreedor.data.id, payload);
            } else {
                resp = await crearAcreedorJWT(idExpediente, payload);
            }
            if (resp?.ok) {
                setSnackbar({ open: true, message: 'Acreedor guardado correctamente', severity: 'success' });
                // Reload acreedores for the grilla
                const reloaded = await expedienteAcreedoresJWT(idExpediente, dialogAcreedor.grilla);
                if (dialogAcreedor.grilla === 'C') setAcreedoresConcurso(normalizeKeys(reloaded?.data || []));
                else setAcreedoresQuiebra(normalizeKeys(reloaded?.data || []));
            } else {
                setSnackbar({ open: true, message: resp?.mensaje || 'Error al guardar', severity: 'error' });
            }
        } catch (error) {
            console.error('Error al guardar acreedor:', error);
            setSnackbar({ open: true, message: 'Error al guardar acreedor', severity: 'error' });
        }
        setDialogAcreedor({ open: false, grilla: '', modo: 'crear', data: null });
    };

    const handleEliminarAcreedor = (acreedor, grilla) => {
        setEliminarDialog({
            open: true,
            nombre: acreedor.acreedor || '',
            onConfirm: async () => {
                setEliminarDialog(prev => ({ ...prev, open: false }));
                try {
                    const resp = await eliminarAcreedorJWT(idExpediente, acreedor.id);
                    if (resp?.ok) {
                        setSnackbar({ open: true, message: 'Acreedor eliminado', severity: 'success' });
                        const reloaded = await expedienteAcreedoresJWT(idExpediente, grilla);
                        if (grilla === 'C') setAcreedoresConcurso(normalizeKeys(reloaded?.data || []));
                        else setAcreedoresQuiebra(normalizeKeys(reloaded?.data || []));
                    } else {
                        setSnackbar({ open: true, message: resp?.mensaje || 'Error al eliminar', severity: 'error' });
                    }
                } catch (error) {
                    console.error('Error al eliminar acreedor:', error);
                    setSnackbar({ open: true, message: 'Error al eliminar acreedor', severity: 'error' });
                }
            }
        });
    };

    // ============================================
    // FORMATEO DE MONTOS
    // ============================================
    const formatMonto = (monto) => {
        if (!monto) return '-';
        return new Intl.NumberFormat('es-AR', {
            style: 'currency',
            currency: 'ARS',
            minimumFractionDigits: 2
        }).format(monto);
    };

    // ============================================
    // RENDER
    // ============================================
    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* ============================================ */}
            {/* SECCIÓN 1: DATOS DEL EXPEDIENTE */}
            {/* ============================================ */}
            <Paper elevation={2} style={{ marginBottom: 16, borderRadius: 4, overflow: 'hidden' }}>
                <Box
                    style={{
                        background: tipo === 'judicial'
                            ? 'linear-gradient(90deg, #1976d2 0%, #1565c0 100%)'
                            : 'linear-gradient(90deg, #d32f2f 0%, #c62828 100%)',
                        color: 'white',
                        padding: 16,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <Box display="flex" alignItems="center">
                        <AccountBalanceIcon style={{ marginRight: 8 }} />
                        <Typography variant="h6" style={{ fontWeight: 600 }}>
                            Datos del Expediente
                        </Typography>
                    </Box>
                    <Box display="flex" gap={1}>
                        {existe && !modoEdicion && (
                            <Tooltip title={perm.update ? "Editar" : "No tenés permiso para editar"}>
                                <span>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<EditIcon />}
                                        onClick={() => setModoEdicion(true)}
                                        disabled={!perm.update}
                                        style={{
                                            backgroundColor: 'white',
                                            color: tipo === 'judicial' ? '#1976d2' : '#d32f2f',
                                            textTransform: 'none',
                                            fontWeight: 600
                                        }}
                                    >
                                        Editar
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                        {modoEdicion && (
                            <>
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<SaveIcon />}
                                    onClick={handleGuardarDatos}
                                    style={{
                                        backgroundColor: '#4caf50',
                                        color: 'white',
                                        textTransform: 'none',
                                        fontWeight: 600
                                    }}
                                >
                                    Guardar
                                </Button>
                                {existe && (
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={handleCancelar}
                                        style={{
                                            backgroundColor: '#f44336',
                                            color: 'white',
                                            textTransform: 'none',
                                            fontWeight: 600
                                        }}
                                    >
                                        Cancelar
                                    </Button>
                                )}
                            </>
                        )}
                        <IconButton
                            size="small"
                            onClick={() => setExpandedSections(prev => ({ ...prev, datos: !prev.datos }))}
                            style={{ color: 'white' }}
                        >
                            {expandedSections.datos ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Box>
                </Box>

                <Collapse in={expandedSections.datos}>
                    <Box p={3}>
                        <Grid container spacing={3}>
                            {/* Datos del Síndico */}
                            <Grid size={12}>
                                <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8, color: '#555' }}>
                                    Datos del Síndico
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Nombre y Apellido"
                                    value={formData.sindico_nombre}
                                    variant="outlined"
                                    size="small"
                                    disabled
                                    helperText="Se obtiene automáticamente de la pestaña Partes"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Domicilio"
                                    value={formData.sindico_domicilio}
                                    variant="outlined"
                                    size="small"
                                    disabled
                                    helperText="Se obtiene automáticamente de la pestaña Partes"
                                />
                            </Grid>

                            {/* Propuesta y Resultado */}
                            <Grid size={12}>
                                <Typography variant="subtitle2" style={{ fontWeight: 700, marginTop: 16, marginBottom: 8, color: '#555' }}>
                                    Propuesta y Resultado del Acuerdo
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Propuesta"
                                    value={formData.propuesta}
                                    onChange={(e) => setFormData({ ...formData, propuesta: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    multiline
                                    rows={4}
                                    disabled={!modoEdicion}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Resultado del Acuerdo"
                                    value={formData.resultado_acuerdo}
                                    onChange={(e) => setFormData({ ...formData, resultado_acuerdo: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    multiline
                                    rows={4}
                                    disabled={!modoEdicion}
                                />
                            </Grid>

                            {/* Fechas - Concurso Preventivo */}
                            <Grid size={12}>
                                <Typography variant="subtitle2" style={{ fontWeight: 700, marginTop: 16, marginBottom: 8, color: '#555' }}>
                                    Fechas - Concurso Preventivo
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="F. Presentación Concurso Preventivo"
                                    type="date"
                                    value={formData.f_presentacion_concurso_preventivo || ''}
                                    onChange={(e) => setFormData({ ...formData, f_presentacion_concurso_preventivo: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    disabled={!modoEdicion}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="F. Sentencia Apertura Concurso"
                                    type="date"
                                    value={formData.f_sentencia_concurso_preventivo || ''}
                                    onChange={(e) => setFormData({ ...formData, f_sentencia_concurso_preventivo: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    disabled={!modoEdicion}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="F. Período de Exclusividad"
                                    type="date"
                                    value={formData.f_periodo_exclusividad || ''}
                                    onChange={(e) => setFormData({ ...formData, f_periodo_exclusividad: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    disabled={!modoEdicion}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="F. Período Informativo"
                                    type="date"
                                    value={formData.f_periodo_informativo || ''}
                                    onChange={(e) => setFormData({ ...formData, f_periodo_informativo: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    disabled={!modoEdicion}
                                />
                            </Grid>

                            {/* Fechas - Quiebra */}
                            <Grid size={12}>
                                <Typography variant="subtitle2" style={{ fontWeight: 700, marginTop: 16, marginBottom: 8, color: '#555' }}>
                                    Fechas - Quiebra
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="F. Sentencia Declaración Quiebra"
                                    type="date"
                                    value={formData.f_sentencia_declaracion_quiebra || ''}
                                    onChange={(e) => setFormData({ ...formData, f_sentencia_declaracion_quiebra: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    disabled={!modoEdicion}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="F. Sentencia Quiebra Indirecta"
                                    type="date"
                                    value={formData.f_sentencia_declaracion_quiebra_indirecta || ''}
                                    onChange={(e) => setFormData({ ...formData, f_sentencia_declaracion_quiebra_indirecta: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    disabled={!modoEdicion}
                                />
                            </Grid>

                            {/* Fechas - Verificación de Créditos */}
                            <Grid size={12}>
                                <Typography variant="subtitle2" style={{ fontWeight: 700, marginTop: 16, marginBottom: 8, color: '#555' }}>
                                    Fechas - Verificación de Créditos
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="F. Límite Verificación Crédito"
                                    type="date"
                                    value={formData.f_limite_verificacion_credito || ''}
                                    onChange={(e) => setFormData({ ...formData, f_limite_verificacion_credito: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    disabled={!modoEdicion}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="F. Presentación Informe Individual"
                                    type="date"
                                    value={formData.f_presentacion_informe_individual || ''}
                                    onChange={(e) => setFormData({ ...formData, f_presentacion_informe_individual: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    disabled={!modoEdicion}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="F. Presentación Informe General"
                                    type="date"
                                    value={formData.f_presentacion_informe_general || ''}
                                    onChange={(e) => setFormData({ ...formData, f_presentacion_informe_general: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    disabled={!modoEdicion}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="F. Sentencia Verificación"
                                    type="date"
                                    value={formData.f_sentencia_verificacion || ''}
                                    onChange={(e) => setFormData({ ...formData, f_sentencia_verificacion: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    disabled={!modoEdicion}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="F. Sentencia Verificación Crédito"
                                    type="date"
                                    value={formData.f_sentencia_verificacion_credito || ''}
                                    onChange={(e) => setFormData({ ...formData, f_sentencia_verificacion_credito: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    disabled={!modoEdicion}
                                />
                            </Grid>

                            {/* Fechas - Recursos */}
                            <Grid size={12}>
                                <Typography variant="subtitle2" style={{ fontWeight: 700, marginTop: 16, marginBottom: 8, color: '#555' }}>
                                    Fechas - Recursos y Concurso Especial
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="F. Iniciación Concurso Especial"
                                    type="date"
                                    value={formData.f_iniciacion_concurso_especial || ''}
                                    onChange={(e) => setFormData({ ...formData, f_iniciacion_concurso_especial: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    disabled={!modoEdicion}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="F. Presentación Recurso Revisión"
                                    type="date"
                                    value={formData.f_presentacion_recurso_revision || ''}
                                    onChange={(e) => setFormData({ ...formData, f_presentacion_recurso_revision: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    disabled={!modoEdicion}
                                />
                            </Grid>

                            {/* Incidentes */}
                            <Grid size={12}>
                                <Typography variant="subtitle2" style={{ fontWeight: 700, marginTop: 16, marginBottom: 8, color: '#555' }}>
                                    Incidentes
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Incidente Verificación Crédito Postconcursal"
                                    value={formData.incidente_verificacion_credito_posconcursal}
                                    onChange={(e) => setFormData({ ...formData, incidente_verificacion_credito_posconcursal: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    disabled={!modoEdicion}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Incidente Recurso de Revisión"
                                    value={formData.incidente_recurso_revision}
                                    onChange={(e) => setFormData({ ...formData, incidente_recurso_revision: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    disabled={!modoEdicion}
                                />
                            </Grid>

                            {/* Observaciones */}
                            <Grid size={12}>
                                <TextField
                                    fullWidth
                                    label="Observaciones"
                                    value={formData.observaciones}
                                    onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    multiline
                                    rows={3}
                                    disabled={!modoEdicion}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Collapse>
            </Paper>

            {/* ============================================ */}
            {/* SECCIÓN 2: ACREEDORES CONCURSO */}
            {/* ============================================ */}
            <Paper elevation={2} style={{ marginBottom: 16, borderRadius: 4, overflow: 'hidden' }}>
                <Box
                    style={{
                        background: 'linear-gradient(90deg, #43a047 0%, #388e3c 100%)',
                        color: 'white',
                        padding: 16,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <Box display="flex" alignItems="center">
                        <AssessmentIcon style={{ marginRight: 8 }} />
                        <Typography variant="h6" style={{ fontWeight: 600 }}>
                            Acreedores del Concurso
                        </Typography>
                    </Box>
                    <Box display="flex" gap={1}>
                        <Tooltip title={perm.insert ? "Agregar acreedor" : "No tenés permiso para agregar"}>
                            <span>
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => handleAbrirDialogAcreedor('C')}
                                    disabled={!perm.insert}
                                    style={{
                                        backgroundColor: 'white',
                                        color: '#43a047',
                                        textTransform: 'none',
                                        fontWeight: 600
                                    }}
                                >
                                    Agregar
                                </Button>
                            </span>
                        </Tooltip>
                        <IconButton
                            size="small"
                            onClick={() => setExpandedSections(prev => ({ ...prev, concurso: !prev.concurso }))}
                            style={{ color: 'white' }}
                        >
                            {expandedSections.concurso ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Box>
                </Box>

                <Collapse in={expandedSections.concurso}>
                    <Box p={2}>
                        {acreedoresConcurso.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" align="center" style={{ padding: 20 }}>
                                No hay acreedores registrados en el Concurso
                            </Typography>
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                                        <TableCell style={{ fontWeight: 700 }}>Acreedor</TableCell>
                                        <TableCell align="right" style={{ fontWeight: 700 }}>Monto</TableCell>
                                        <TableCell style={{ fontWeight: 700 }}>Privilegio</TableCell>
                                        <TableCell style={{ fontWeight: 700 }}>Quirografario</TableCell>
                                        <TableCell align="center" style={{ fontWeight: 700 }}>Acciones</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {acreedoresConcurso.map((acreedor, index) => (
                                        <TableRow key={acreedor.id} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#fafafa' }}>
                                            <TableCell>{acreedor.acreedor}</TableCell>
                                            <TableCell align="right">{formatMonto(acreedor.monto)}</TableCell>
                                            <TableCell>{acreedor.privilegio || '-'}</TableCell>
                                            <TableCell>{acreedor.quirografario || '-'}</TableCell>
                                            <TableCell align="center">
                                                <Tooltip title={perm.update ? "Editar" : "No tenés permiso"}>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleAbrirDialogAcreedor('C', acreedor)}
                                                            disabled={!perm.update}
                                                        >
                                                            <EditIcon fontSize="small" style={{ color: '#1976d2' }} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title={perm.delete ? "Eliminar" : "No tenés permiso"}>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleEliminarAcreedor(acreedor, 'C')}
                                                            disabled={!perm.delete}
                                                        >
                                                            <DeleteIcon fontSize="small" style={{ color: '#d32f2f' }} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Box>
                </Collapse>
            </Paper>

            {/* ============================================ */}
            {/* SECCIÓN 3: ACREEDORES QUIEBRA */}
            {/* ============================================ */}
            <Paper elevation={2} style={{ marginBottom: 16, borderRadius: 4, overflow: 'hidden' }}>
                <Box
                    style={{
                        background: 'linear-gradient(90deg, #e53935 0%, #c62828 100%)',
                        color: 'white',
                        padding: 16,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <Box display="flex" alignItems="center">
                        <AssessmentIcon style={{ marginRight: 8 }} />
                        <Typography variant="h6" style={{ fontWeight: 600 }}>
                            Acreedores de la Quiebra
                        </Typography>
                    </Box>
                    <Box display="flex" gap={1}>
                        <Tooltip title={perm.insert ? "Agregar acreedor" : "No tenés permiso para agregar"}>
                            <span>
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => handleAbrirDialogAcreedor('Q')}
                                    disabled={!perm.insert}
                                    style={{
                                        backgroundColor: 'white',
                                        color: '#e53935',
                                        textTransform: 'none',
                                        fontWeight: 600
                                    }}
                                >
                                    Agregar
                                </Button>
                            </span>
                        </Tooltip>
                        <IconButton
                            size="small"
                            onClick={() => setExpandedSections(prev => ({ ...prev, quiebra: !prev.quiebra }))}
                            style={{ color: 'white' }}
                        >
                            {expandedSections.quiebra ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Box>
                </Box>

                <Collapse in={expandedSections.quiebra}>
                    <Box p={2}>
                        {acreedoresQuiebra.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" align="center" style={{ padding: 20 }}>
                                No hay acreedores registrados en la Quiebra
                            </Typography>
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                                        <TableCell style={{ fontWeight: 700 }}>Acreedor</TableCell>
                                        <TableCell align="right" style={{ fontWeight: 700 }}>Monto</TableCell>
                                        <TableCell style={{ fontWeight: 700 }}>Privilegio</TableCell>
                                        <TableCell style={{ fontWeight: 700 }}>Quirografario</TableCell>
                                        <TableCell align="center" style={{ fontWeight: 700 }}>Acciones</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {acreedoresQuiebra.map((acreedor, index) => (
                                        <TableRow key={acreedor.id} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#fafafa' }}>
                                            <TableCell>{acreedor.acreedor}</TableCell>
                                            <TableCell align="right">{formatMonto(acreedor.monto)}</TableCell>
                                            <TableCell>{acreedor.privilegio || '-'}</TableCell>
                                            <TableCell>{acreedor.quirografario || '-'}</TableCell>
                                            <TableCell align="center">
                                                <Tooltip title={perm.update ? "Editar" : "No tenés permiso"}>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleAbrirDialogAcreedor('Q', acreedor)}
                                                            disabled={!perm.update}
                                                        >
                                                            <EditIcon fontSize="small" style={{ color: '#1976d2' }} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title={perm.delete ? "Eliminar" : "No tenés permiso"}>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleEliminarAcreedor(acreedor, 'Q')}
                                                            disabled={!perm.delete}
                                                        >
                                                            <DeleteIcon fontSize="small" style={{ color: '#d32f2f' }} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Box>
                </Collapse>
            </Paper>

            {/* ============================================ */}
            {/* DIALOG ACREEDOR */}
            {/* ============================================ */}
            <Dialog
                open={dialogAcreedor.open}
                onClose={() => setDialogAcreedor({ open: false, grilla: '', modo: 'crear', data: null })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle style={{ borderBottom: '1px solid #e0e0e0', backgroundColor: '#f5f5f5' }}>
                    <Typography variant="h6" style={{ fontWeight: 600 }}>
                        {dialogAcreedor.modo === 'crear' ? 'Agregar' : 'Editar'} Acreedor - {dialogAcreedor.grilla === 'C' ? 'Concurso' : 'Quiebra'}
                    </Typography>
                </DialogTitle>
                <DialogContent style={{ paddingTop: 24 }}>
                    <Grid container spacing={2}>
                        <Grid size={12}>
                            <TextField
                                fullWidth
                                label="Acreedor"
                                value={formAcreedor.acreedor}
                                onChange={(e) => setFormAcreedor({ ...formAcreedor, acreedor: e.target.value })}
                                variant="outlined"
                                size="small"
                                required
                                inputProps={{ maxLength: 100 }}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                fullWidth
                                label="Monto"
                                type="number"
                                value={formAcreedor.monto}
                                onChange={(e) => setFormAcreedor({ ...formAcreedor, monto: e.target.value })}
                                variant="outlined"
                                size="small"
                                required
                                inputProps={{ min: 0, step: 0.01 }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                label="Privilegio"
                                value={formAcreedor.privilegio}
                                onChange={(e) => setFormAcreedor({ ...formAcreedor, privilegio: e.target.value })}
                                variant="outlined"
                                size="small"
                                inputProps={{ maxLength: 20 }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                label="Quirografario"
                                value={formAcreedor.quirografario}
                                onChange={(e) => setFormAcreedor({ ...formAcreedor, quirografario: e.target.value })}
                                variant="outlined"
                                size="small"
                                inputProps={{ maxLength: 20 }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions style={{ padding: '16px 24px', borderTop: '1px solid #e0e0e0', backgroundColor: '#f5f5f5' }}>
                    <Button
                        onClick={() => setDialogAcreedor({ open: false, grilla: '', modo: 'crear', data: null })}
                        color="secondary"
                        style={{ textTransform: 'none' }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleGuardarAcreedor}
                        color="primary"
                        variant="contained"
                        style={{ minWidth: 120, textTransform: 'none', fontWeight: 600 }}
                    >
                        Guardar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ============================================ */}
            {/* DIALOG CONFIRMAR ELIMINAR */}
            <Dialog
                open={eliminarDialog.open}
                onClose={() => setEliminarDialog(prev => ({ ...prev, open: false }))}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '4px' } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#d32f2f' }}>
                    <WarningIcon /> Confirmar eliminación
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        ¿Estás seguro de que querés eliminar al acreedor <strong>{eliminarDialog.nombre}</strong>?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setEliminarDialog(prev => ({ ...prev, open: false }))} color="inherit">
                        Cancelar
                    </Button>
                    <Button onClick={eliminarDialog.onConfirm} variant="contained" color="error">
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* SNACKBAR */}
            {/* ============================================ */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <MuiAlert
                    elevation={6}
                    variant="filled"
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    {snackbar.message}
                </MuiAlert>
            </Snackbar>
        </Box>
    );
};

export default ConcursosTab;